import React, { FC, useCallback, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { css, cx } from '@emotion/css';
import copy from 'clipboard-copy';
import { compose } from 'redux';

import {
    AccountName,
    AccountTitle,
    Avatar,
    FlexColumn,
    FlexRow,
    Image2,
    LinkContainer,
    SideMenuLink,
    Small,
    Tooltip,
} from '@portal/common/components';
import { isDataToolAccessible } from '@portal/common/models/data-project';
import { withGoogleAnalytics } from '@portal/common/services/google-analytics';
import { styled, default as theme, withMedia } from '@portal/common/theme';
import { SidebarLinkResource } from '@portal/common/types';
import getAppVersionText from '@portal/common/utility/get-app-version-text';

import _ from '../../locale';
import { getDataProjectIdsForTool } from '../../models/data-tool';
import {
    CONTACT_US_PATH,
    DATA_LICENSE_PATH,
    DEFINITIONS_PATH,
    FAVORITE_CONTENT_PATH,
    HOME_PATH,
    IHME_STAFF_PATH,
    METHODOLOGY_PATH,
    PROFILE_GENERAL_ID,
    PROFILE_PATH,
    SOURCES_PATH,
    TEAM_PATH,
} from '../../router/paths';
import { getDataCollections } from '../../store/data-explorer/selectors';
import { getSidebarLinks } from '../../store/resources/selectors';
import { getSessionAccount } from '../../store/session/selectors';
import { toggleMinimizeSidebar } from '../../store/user-settings/actions';
import { getIsSidebarMinimized } from '../../store/user-settings/selectors';
import MinimizeSidebarButton from './MinimizeSidebarButton';
import dataToolsConfig from './data-tools-config';
import ListsRoute from './lists/ListsRoute';

const infoIcon = <FaInfoCircle />;

const Container = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
});

const accountLinkStyles = {
    minHeight: 'auto',
    fontWeight: 700,
};

const Sidenav = styled('div')<{ minimized: boolean }>(({ theme, minimized }) => ({
    color: '#fff',
    background: theme.color.jetBlack,
    display: 'flex',
    position: 'relative',
    flexFlow: 'column nowrap',
    width: minimized ? theme.minWidth.navigationMinimized : theme.minWidth.navigation,
    minWidth: minimized ? theme.minWidth.navigationMinimized : theme.minWidth.navigation,
    ...(minimized && {
        alignItems: 'center',
    }),
}));

const SidenavGroup = styled('div')<{ minimized: boolean }>(({ minimized }) => ({
    flexShrink: 0,
    display: 'flex',
    flexFlow: 'column nowrap',
    padding: minimized ? '0 0 30px 0' : '0px 30px 30px 30px',
}));

const SidenavMainMenu = styled('div')<{ minimized: boolean }>(({ minimized }) => ({
    flexGrow: 1,
    display: 'block',
    padding: minimized ? '0 0 30px 0' : '0 30px 30px 35px',
    overflow: 'hidden',
    ':hover': {
        overflow: 'auto',
    },
}));

const SidenavMenuGroupTitle = styled(Small.withComponent('b'))(({ theme }) => ({
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: '1.2rem',
    color: theme.color.white30,
}));

const LogoContainer = styled(SidenavGroup)({
    paddingTop: 30,
    height: 90,
    img: {
        height: 30,
    },
});

const RowWithInfo = styled(FlexRow)({
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
});

const StyledAvatar = styled(Avatar)({
    cursor: 'pointer',
});

const AccountBlock = styled(FlexColumn)({
    marginLeft: 10,
    width: 155,
});

const accountNameStyles = cx(css`
    width: 155px;
    font-weight: ${theme.typography.fontWeight.bold};
`);

const accountTitleStyles = cx(css`
    width: 155px;
    color: ${theme.color.gray40} !important;
`);

const ProfileLinkContainer = styled(LinkContainer)(({ theme }) => ({
    cursor: 'pointer',
    ':hover': {
        '.account-name': {
            color: theme.color.green1,
        },
    },
}));

const FlexColumnStyled = styled(FlexColumn)<{ minimized?: boolean }>(({ minimized }) => ({
    alignItems: minimized ? 'center' : 'start',
}));

const VersionText = styled('div')<{ minimized?: boolean }>(({ theme, minimized }) => ({
    fontSize: 14,
    color: theme.color.white30,
    cursor: 'pointer',
    display: minimized ? 'none' : 'inline',
    padding: '10px 0',
    textAlign: 'center',
}));

const Link = ({ to, iconSrc, name, isMinimized, isVisible = true, exact = true, ...props }) => {
    if (!isVisible) {
        return null;
    }

    return (
        <LinkContainer exact={exact} to={to}>
            <SideMenuLink
                beforeIconSrc={iconSrc}
                beforeIconAlt={name}
                centered={isMinimized}
                {...props}
            >
                {!isMinimized && <div>{name}</div>}
            </SideMenuLink>
        </LinkContainer>
    );
};

interface SidebarLinkProps {
    resource: SidebarLinkResource;
}

const SidebarLink: FC<SidebarLinkProps> = ({ resource }) => {
    const isSidebarMinimized = useSelector(getIsSidebarMinimized);

    return (
        <SideMenuLink
            key={resource.id}
            target="_blank"
            href={resource.url}
            beforeIconSrc={`/icons/ic-${resource.icon}.svg`}
            beforeIconAlt={resource.title}
            centered={isSidebarMinimized}
        >
            {!isSidebarMinimized && (
                <RowWithInfo>
                    {resource.title}
                    {resource.hint && (
                        <Tooltip interactive title={resource.hint}>
                            <div>{infoIcon}</div>
                        </Tooltip>
                    )}
                </RowWithInfo>
            )}
        </SideMenuLink>
    );
};

const Navigation: FC = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const dataCollections = useSelector(getDataCollections);
    const sessionAccount = useSelector(getSessionAccount);
    const isSidebarMinimized = useSelector(getIsSidebarMinimized);
    const sidebarLinks = useSelector(getSidebarLinks);

    const [isSidebarButtonVisible, setIsSidebarButtonVisible] = useState(false);

    if (!sessionAccount) return null;

    const versionText = getAppVersionText('Client Portal', config.env);

    const handleMouseEnter = useCallback(() => {
        setIsSidebarButtonVisible(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsSidebarButtonVisible(false);
    }, []);

    const handleMinimizeSidebarButtonClick = useCallback(() => {
        dispatch(toggleMinimizeSidebar());
    }, []);

    const handleVersionClick = useCallback(() => {
        copy(versionText);
    }, [versionText]);

    const checkIsLinkActive = useCallback((link: string) => pathname.includes(link), [pathname]);

    const checkIsProfileLinkActive = useCallback(
        () => checkIsLinkActive(PROFILE_PATH),
        [checkIsLinkActive]
    );
    const checkIsFavoritesLinkActive = useCallback(
        () => checkIsLinkActive(FAVORITE_CONTENT_PATH),
        [checkIsLinkActive]
    );

    const logoSrc = isSidebarMinimized ? '/img/logo.png' : '/img/logo-with-text.png';

    return (
        <Container onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <Sidenav minimized={isSidebarMinimized}>
                <MinimizeSidebarButton
                    visible={isSidebarButtonVisible}
                    minimized={isSidebarMinimized}
                    onClick={handleMinimizeSidebarButtonClick}
                />

                <LogoContainer minimized={isSidebarMinimized}>
                    <RouterLink to="/">
                        <Image2 src={logoSrc} />
                    </RouterLink>
                </LogoContainer>

                <SidenavGroup minimized={isSidebarMinimized}>
                    <ProfileLinkContainer to={PROFILE_PATH + PROFILE_GENERAL_ID}>
                        <FlexRow>
                            <FlexColumn data-private>
                                <StyledAvatar src={sessionAccount.avatar_url} />
                            </FlexColumn>
                            {!isSidebarMinimized && (
                                <AccountBlock justify="center" itemsSpacing={4}>
                                    <Link
                                        to={PROFILE_PATH + PROFILE_GENERAL_ID}
                                        checkIsActive={checkIsProfileLinkActive}
                                        name={
                                            <AccountName
                                                className={`${accountNameStyles} account-name`}
                                                account={sessionAccount}
                                            />
                                        }
                                        isMinimized={false}
                                        styles={accountLinkStyles}
                                    />
                                    {sessionAccount.title && (
                                        <AccountTitle
                                            className={accountTitleStyles}
                                            title={sessionAccount.title}
                                        />
                                    )}
                                </AccountBlock>
                            )}
                        </FlexRow>
                    </ProfileLinkContainer>
                </SidenavGroup>

                <SidenavMainMenu minimized={isSidebarMinimized}>
                    <FlexColumn itemsSpacing={30}>
                        <FlexColumnStyled itemsSpacing={5} minimized={isSidebarMinimized}>
                            <SidenavMenuGroupTitle>{_('nav_group_main')}</SidenavMenuGroupTitle>
                            <FlexColumn>
                                <Link
                                    to={HOME_PATH}
                                    iconSrc="/icons/ic-dashboard.svg"
                                    name={_('nav_dashboard')}
                                    isMinimized={isSidebarMinimized}
                                />
                                <Link
                                    to={FAVORITE_CONTENT_PATH}
                                    exact={false}
                                    checkIsActive={checkIsFavoritesLinkActive}
                                    iconSrc="/icons/ic-star.svg"
                                    name={_('nav_my_favorites')}
                                    isMinimized={isSidebarMinimized}
                                />
                            </FlexColumn>
                        </FlexColumnStyled>

                        <FlexColumnStyled itemsSpacing={5} minimized={isSidebarMinimized}>
                            <SidenavMenuGroupTitle>{_('nav_group_data')}</SidenavMenuGroupTitle>
                            <FlexColumn>
                                {dataToolsConfig.map(({ path, iconSrc, labelKey }) => (
                                    <Link
                                        key={path}
                                        to={path}
                                        iconSrc={iconSrc}
                                        name={_(labelKey)}
                                        isMinimized={isSidebarMinimized}
                                        isVisible={isDataToolAccessible(
                                            getDataProjectIdsForTool(path),
                                            dataCollections
                                        )}
                                    />
                                ))}
                            </FlexColumn>
                        </FlexColumnStyled>

                        <FlexColumnStyled itemsSpacing={5} minimized={isSidebarMinimized}>
                            <SidenavMenuGroupTitle>{_('nav_group_ihme')}</SidenavMenuGroupTitle>
                            <FlexColumn>
                                <Link
                                    to={DEFINITIONS_PATH}
                                    iconSrc="/icons/ic-book.svg"
                                    name={_('nav_definitions')}
                                    isMinimized={isSidebarMinimized}
                                />
                                <Link
                                    to={SOURCES_PATH}
                                    iconSrc="/icons/ic-cloud.svg"
                                    name={_('nav_sources')}
                                    isMinimized={isSidebarMinimized}
                                />
                                <Link
                                    to={METHODOLOGY_PATH}
                                    iconSrc="/icons/ic-folder.svg"
                                    name={_('nav_methodology')}
                                    isMinimized={isSidebarMinimized}
                                />
                                <Link
                                    to={IHME_STAFF_PATH}
                                    iconSrc="/icons/ic-user.svg"
                                    name={_('nav_staff')}
                                    isMinimized={isSidebarMinimized}
                                />
                                <Link
                                    to={CONTACT_US_PATH}
                                    iconSrc="/icons/ic-mail.svg"
                                    name={_('nav_contact_us')}
                                    isMinimized={isSidebarMinimized}
                                />
                            </FlexColumn>
                        </FlexColumnStyled>

                        <FlexColumnStyled itemsSpacing={5} minimized={isSidebarMinimized}>
                            <SidenavMenuGroupTitle>
                                {_(
                                    isSidebarMinimized
                                        ? 'nav_group_tools_minimized'
                                        : 'nav_group_tools'
                                )}
                            </SidenavMenuGroupTitle>
                            <FlexColumn>
                                {sidebarLinks &&
                                    sidebarLinks.results.map((link) => (
                                        <SidebarLink key={link.id} resource={link} />
                                    ))}
                            </FlexColumn>
                        </FlexColumnStyled>

                        <FlexColumnStyled itemsSpacing={5} minimized={isSidebarMinimized}>
                            <SidenavMenuGroupTitle>
                                {isSidebarMinimized ? 'ORG.' : _('nav_group_organization')}
                            </SidenavMenuGroupTitle>
                            <FlexColumn>
                                <Link
                                    to={TEAM_PATH}
                                    iconSrc="/icons/ic-team.svg"
                                    name={_('nav_team')}
                                    isMinimized={isSidebarMinimized}
                                />
                                <Link
                                    to={DATA_LICENSE_PATH}
                                    iconSrc="/icons/ic-switches.svg"
                                    name={_('data_license_title')}
                                    isMinimized={isSidebarMinimized}
                                />
                            </FlexColumn>
                        </FlexColumnStyled>
                    </FlexColumn>
                </SidenavMainMenu>
                <VersionText minimized={isSidebarMinimized} onClick={handleVersionClick}>
                    {versionText}
                </VersionText>
            </Sidenav>
            <ListsRoute />
        </Container>
    );
};

export default compose(
    withMedia,
    withGoogleAnalytics({
        trackMethods: (props) => ({
            handleExternalLinkClick: (url, title) =>
                props.trackEvent({
                    category: 'Content',
                    action: 'Open Data Tools Link',
                    label: title,
                }),
        }),
    })
)(Navigation);
